'use client';

import React from 'react';
import { AlertTriangle } from 'lucide-react';

interface AnalyticsErrorBoundaryProps {
    children: React.ReactNode;
    onClose?: () => void;
}

interface AnalyticsErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

export class AnalyticsErrorBoundary extends React.Component<
    AnalyticsErrorBoundaryProps,
    AnalyticsErrorBoundaryState
> {
    constructor(props: AnalyticsErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error) {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        // Only log in development or if we have a logging service
        if (process.env.NODE_ENV === 'development') {
            console.error('[AnalyticsErrorBoundary] Error caught:', error);
            console.error('[AnalyticsErrorBoundary] Error info:', errorInfo);
        }
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                    <AlertTriangle className="h-10 w-10 text-amber-500 mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Analytics Error</h3>
                    <p className="text-sm text-muted-foreground mb-4 max-w-md">
                        {this.state.error?.message || 'An error occurred while loading analytics'}
                    </p>
                    {process.env.NODE_ENV === 'development' && (
                        <pre className="text-xs bg-muted p-3 rounded-lg max-w-full overflow-x-auto text-left mb-4">
                            {this.state.error?.stack?.split('\n').slice(0, 5).join('\n')}
                        </pre>
                    )}
                    <button
                        onClick={() => {
                            this.setState({ hasError: false, error: null });
                            this.props.onClose?.();
                        }}
                        className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm"
                    >
                        Close Panel
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}

